import type { Ereignis, Operation, Person, UiVoll } from "@gui-creator/gemeinsam";
import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { schluessel } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { betroffen, nachAbruf, verarbeite } from "./abgleich.ts";
import { puffer, type Antwort } from "./puffer.ts";
import { oeffneStrom } from "./strom.ts";

type UiEreignis = Extract<Ereignis, { art: "ui" }>;
export type Speicherstand = "gespeichert" | "speichert" | "offline" | "abgemeldet";
export type FremdeAenderung = { ui: string; autor: string; slots: number[]; ui_felder: boolean };

let qc: QueryClient | null = null;
let melde: (text: string) => void = () => {};
let eigenerName = "";
const bestaetigt = new Map<string, number>();
const wartend = new Map<string, UiEreignis[]>();
const fremdHoerer = new Set<(f: FremdeAenderung) => void>();
const nachladen = new Map<string, number>();
const nachDemSenden = new Set<string>();
let warVerbunden = false;
let wiederholung: number | undefined;

let stand: Speicherstand = "gespeichert";
const standHoerer = new Set<() => void>();
function setzeStand(s: Speicherstand) {
  if (s === stand) return;
  stand = s;
  standHoerer.forEach((h) => h());
}
export function useSpeicherstand(): Speicherstand {
  return useSyncExternalStore(
    (h) => (standHoerer.add(h), () => standHoerer.delete(h)),
    () => stand,
  );
}

export function aufFremdeAenderung(h: (f: FremdeAenderung) => void): () => void {
  fremdHoerer.add(h);
  return () => fremdHoerer.delete(h);
}

const sende = (ui: string, ops: Operation[]) => api<Antwort>(`/api/uis/${ui}/operationen`, { methode: "POST", body: { operationen: ops } });

function spaeterNeuLaden(ui: string) {
  window.clearTimeout(nachladen.get(ui));
  nachladen.set(
    ui,
    window.setTimeout(() => {
      if (!puffer.ausstehendFuer(ui)) void qc?.invalidateQueries({ queryKey: schluessel.ui(ui) });
    }, 400),
  );
}

/** Ein Abruf ersetzt die Ansicht; noch nicht bestätigte eigene Änderungen kommen wieder obendrauf. */
function nachUiAbruf(ui: string, daten: UiVoll | undefined) {
  if (!qc || !daten || !puffer.ausstehendFuer(ui)) return;
  const r = nachAbruf({ revision: daten.revision, daten: daten.daten }, bestaetigt.get(ui), puffer.opsFuer(ui));
  if (r === "spaeter") nachDemSenden.add(ui);
  else qc.setQueryData<UiVoll>(schluessel.ui(ui), { ...daten, daten: r.daten });
}

/** Merkt die vom Server bestätigte Revision, sobald nichts mehr unterwegs ist. */
export function uiGeladen(ui: string, revision: number) {
  if (!puffer.ausstehendFuer(ui)) bestaetigt.set(ui, revision);
}

function uiEreignis(e: UiEreignis) {
  if (!qc) return;
  const key = schluessel.ui(e.id);
  const daten = qc.getQueryData<UiVoll>(key);
  if (!daten) return;
  if (puffer.ausstehendFuer(e.id)) {
    wartend.set(e.id, [...(wartend.get(e.id) ?? []), e]);
    return;
  }
  const basis = bestaetigt.get(e.id) ?? daten.revision;
  const r = verarbeite({ revision: basis, daten: daten.daten }, e);
  if (r === "neu_laden") {
    void qc.invalidateQueries({ queryKey: key });
    return;
  }
  if (r.revision === basis) return;
  bestaetigt.set(e.id, r.revision);
  qc.setQueryData<UiVoll>(key, { ...daten, daten: r.daten, revision: r.revision, status: e.status });
  if (e.autor !== eigenerName) {
    const b = betroffen(e.operationen);
    fremdHoerer.forEach((h) => h({ ui: e.id, autor: e.autor, slots: b.slots, ui_felder: b.ui }));
  }
  spaeterNeuLaden(e.id);
}

/** Schickt den Puffer ab; bei Netzausfall erneut, bei Abmeldung bleibt er liegen. */
export async function spuele(): Promise<void> {
  if (!qc || puffer.leer()) return setzeStand(stand === "abgemeldet" ? "abgemeldet" : "gespeichert");
  setzeStand("speichert");
  const neuLaden = new Set<string>();
  const ergebnis = await puffer.senden(sende, (ui, a) => {
    const alt = qc?.getQueryData<UiVoll>(schluessel.ui(ui));
    if (alt && a.status && a.status !== alt.status) neuLaden.add(ui);
    qc?.setQueryData<UiVoll>(schluessel.ui(ui), (v) => (v ? { ...v, revision: a.revision, befunde: a.befunde, status: a.status ?? v.status } : v));
    const b = bestaetigt.get(ui);
    if (b !== undefined && a.revision !== b + 1) neuLaden.add(ui);
    bestaetigt.set(ui, a.revision);
  });
  for (const ui of neuLaden) void qc.invalidateQueries({ queryKey: schluessel.ui(ui) });
  if (ergebnis === "offline") {
    setzeStand("offline");
    window.clearTimeout(wiederholung);
    wiederholung = window.setTimeout(() => void spuele(), 4000);
    return;
  }
  if (ergebnis === "abgemeldet") return setzeStand("abgemeldet");
  if (ergebnis === "ui_weg") melde("Die UI wurde inzwischen gelöscht; die letzten Änderungen wurden verworfen.");
  if (ergebnis === "abgelehnt") {
    melde(puffer.letzterFehler ?? "Eine Änderung wurde abgelehnt.");
    if (puffer.abgelehntFuer) nachDemSenden.add(puffer.abgelehntFuer);
  }
  if (ergebnis !== "ok") {
    for (const ui of wartend.keys()) void qc.invalidateQueries({ queryKey: schluessel.ui(ui) });
    return spuele();
  }
  setzeStand("gespeichert");
  for (const ui of nachDemSenden) void qc.invalidateQueries({ queryKey: schluessel.ui(ui) });
  nachDemSenden.clear();
  for (const [ui, liste] of wartend) {
    wartend.delete(ui);
    liste.forEach(uiEreignis);
  }
}

/** Sendet alles Ausstehende; false, wenn danach noch Änderungen liegen bleiben. */
export async function allesGespeichert(): Promise<boolean> {
  await spuele();
  return puffer.leer();
}

export function einreihen(ui: string, ops: Operation[]) {
  puffer.hinzu(ui, ops);
  void spuele();
}

/** Ereignisstrom für die ganze Hülle; nach einer Unterbrechung wird alles neu geladen. */
export function verbinde(q: QueryClient, konto: { kontoId: string; name: string }, hinweis: (text: string) => void): () => void {
  qc = q;
  eigenerName = konto.name;
  melde = hinweis;
  const verworfen = puffer.besitzerSetzen(konto.kontoId);
  if (verworfen) {
    wartend.clear();
    hinweis(`${verworfen} nicht gespeicherte ${verworfen === 1 ? "Änderung" : "Änderungen"} eines anderen Kontos wurden verworfen.`);
  }
  if (stand === "abgemeldet") setzeStand(puffer.leer() ? "gespeichert" : "speichert");
  void spuele();
  const nachricht = (roh: string) => {
    const e = JSON.parse(roh) as Ereignis;
    if (e.art === "ui") {
      void q.invalidateQueries({ queryKey: schluessel.verlauf(e.id) });
      return uiEreignis(e);
    }
    if (e.art === "ui_meta") {
      for (const k of [schluessel.ui(e.id), schluessel.staende(e.id), schluessel.verlauf(e.id)]) void q.invalidateQueries({ queryKey: k });
      return;
    }
    if (e.art === "struktur") return void q.invalidateQueries({ queryKey: schluessel.struktur });
    if (e.art === "katalog") return void q.invalidateQueries({ queryKey: schluessel.katalog(e.plugin) });
    if (e.art === "notizen") {
      void q.invalidateQueries({ queryKey: schluessel.notizen(e.ziel.art, e.ziel.id) });
      void q.invalidateQueries({ queryKey: ["todos"] });
      return;
    }
    if (e.art === "anwesenheit") q.setQueryData<Person[]>(["anwesenheit", e.ui], e.personen);
  };
  const schliessen = oeffneStrom("/api/ereignisse", {
    offen: () => {
      if (warVerbunden) void q.invalidateQueries();
      warVerbunden = true;
      void spuele();
    },
    nachricht,
  });
  const abo = q.getQueryCache().subscribe((ev) => {
    if (ev.type !== "updated" || ev.action.type !== "success" || ev.action.manual) return;
    const [art, ui] = ev.query.queryKey;
    if (art === "ui" && typeof ui === "string") nachUiAbruf(ui, ev.action.data as UiVoll | undefined);
  });
  const online = () => void spuele();
  window.addEventListener("online", online);
  return () => {
    window.removeEventListener("online", online);
    abo();
    schliessen();
    warVerbunden = false;
  };
}
